import { cn } from "@lib/cn";
import g2Badges from "@/assets/brand/g2-badges-summer-2026.png";
import logoAcs from "@/assets/brand/logo-acs.png";
import logoCarta from "@/assets/brand/logo-carta.png";
import logoIheartmedia from "@/assets/brand/logo-iheartmedia.png";
import logoMeta from "@/assets/brand/logo-meta.png";
import logoNice from "@/assets/brand/logo-nice.png";
import logoXerox from "@/assets/brand/logo-xerox.png";
import teamAvatar1 from "@/assets/brand/team-avatar-1.png";
import teamAvatar2 from "@/assets/brand/team-avatar-2.png";
import teamAvatar3 from "@/assets/brand/team-avatar-3.png";
import teamAvatar4 from "@/assets/brand/team-avatar-4.png";
import { track } from "@/features/analytics";
import {
  useGetCurrentUserQuery,
  useVerifyTeamWalkthroughMutation,
} from "@/shared/services";
import Button from "@components/ui/button";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import {
  IconApps,
  IconCalendarTime,
  IconCircleX,
  IconPalette,
  IconSparkles,
  IconTools,
  IconUserStar,
  IconVariable,
} from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import "../styles/index.scss";

/** Two columns read top to bottom in the design; the grid fills row by row, so pairs interleave. */
const FEATURES = [
  {
    icon: IconVariable,
    lines: ["Unlimited Smart Variables", "and custom variables"],
  },
  {
    icon: IconPalette,
    lines: ["Advanced branding", "(custom font, subdomain)"],
  },
  { icon: IconApps, lines: ["Integrations & automations,", "incl. CRM"] },
  { icon: IconUserStar, lines: ["Team roles & permissions"] },
  { icon: IconSparkles, lines: ["Extended AI usage credits"] },
  { icon: IconTools, lines: ["Tailor-made templates by", "our studio"] },
] as const;

const COMPANY_LOGOS = [
  { src: logoCarta, alt: "Carta", className: "tw-h-[25px] tw-w-auto" },
  { src: logoIheartmedia, alt: "iHeartMedia" },
  { src: logoMeta, alt: "Meta" },
  { src: logoNice, alt: "NICE" },
  { src: logoAcs, alt: "American Cancer Society" },
  { src: logoXerox, alt: "Xerox" },
] as const;

/** Overlapped left-to-right, so only the last one has no negative margin. */
const RATING_AVATARS = [teamAvatar1, teamAvatar2, teamAvatar3, teamAvatar4];

const SHELL_CLASS =
  "tw-relative tw-flex tw-flex-col tw-rounded-2xl tw-bg-[#FBFBFC] tw-font-sans tw-shadow-xl";

const EYEBROW_CLASS =
  "tw-m-0 tw-text-xs tw-font-semibold tw-uppercase tw-leading-5 tw-tracking-[2px]";

const HUBSPOT_MEETINGS_URL = "https://meetings.hubspot.com/daveh5";

const buildMeetingsSrc = (email?: string, firstName?: string) => {
  const params = new URLSearchParams({
    uuid: "3ce9c8aa-8c9a-46f5-9e9a-c333641e1499",
    embed: "true",
  });
  if (email) params.set("email", email);
  if (firstName) params.set("firstname", firstName);
  return `${HUBSPOT_MEETINGS_URL}?${params.toString()}`;
};

const HUBSPOT_MEETINGS_ORIGIN =
  /^https:\/\/meetings(-[a-z0-9]+)?\.hubspot\.com$/;

const UpgradeToTeamDialog = ({ source }: { source: string }) => {
  const dispatch = useAppDispatch();
  const [isOverlayVisible, setIsOverlayVisible] = useState(true);
  const { data: me } = useGetCurrentUserQuery();
  const meetingsSrc = buildMeetingsSrc(me?.email, me?.firstName);

  const close = () => {
    dispatch(closeDialog("upgrade-to-team"));
  };

  useEffect(() => {
    document.dispatchEvent(
      new CustomEvent("updatePlanPropertiesInfo", {
        detail: {
          trigger: "teamTriggerTimestamp",
          value: new Date().toISOString(),
          action: "Open team dialog",
          payload: { source },
        },
      }),
    );
    track("open team dialog", { "lock feature source": source });
    track("view popup team plan", {
      "plan name": "Team",
    });
  }, []);

  const { mutate: verifyTeamWalkthrough } = useVerifyTeamWalkthroughMutation();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const hasVerifiedRef = useRef(false);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!HUBSPOT_MEETINGS_ORIGIN.test(event.origin)) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (hasVerifiedRef.current) return;
      if (!event.data?.meetingBookSucceeded) return;
      hasVerifiedRef.current = true;
      verifyTeamWalkthrough();
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [verifyTeamWalkthrough]);

  return (
    <div
      className={
        isOverlayVisible
          ? `${SHELL_CLASS} tw-w-[672px] tw-gap-10 tw-px-6 tw-py-8`
          : // The booking widget renders its own bordered card with internal
            // margin, so the shell keeps only enough padding to clear it.
            `${SHELL_CLASS} tw-w-[689px] tw-px-3 tw-pb-3 tw-pt-8`
      }
    >
      {/*
        The HubSpot iframe below is mounted unconditionally and only ever
        hidden with display:none (tw-hidden). It must stay mounted across
        the stage transition so the booking widget warm-loads while the
        marketing stage is visible instead of loading after the user clicks
        through — do not switch this back to conditional rendering.
      */}
      <div
        className={
          isOverlayVisible
            ? "tw-hidden"
            : "tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-8"
        }
      >
        <button
          type="button"
          aria-label="Close"
          onClick={close}
          tabIndex={isOverlayVisible ? -1 : undefined}
          className="tw-absolute tw-right-2 tw-top-2 tw-flex tw-size-6 tw-cursor-pointer tw-items-center tw-justify-center tw-border-none tw-bg-[transparent] tw-p-0 tw-text-blue-500 tw-transition-opacity hover:tw-opacity-70"
        >
          <IconCircleX className="tw-size-6 !tw-fill-none" />
        </button>
        <h2 className="tw-m-0 tw-w-full tw-text-center tw-text-2xl tw-font-semibold tw-leading-8 tw-text-blue">
          Book a quick call to learn more about the Team plans
        </h2>
        <iframe
          ref={iframeRef}
          title="Book a demo"
          src={meetingsSrc}
          tabIndex={isOverlayVisible ? -1 : undefined}
          className="tw-h-[603px] tw-w-full tw-border-0"
        />
      </div>

      <div className={isOverlayVisible ? "tw-contents" : "tw-hidden"}>
        <div className="tw-flex tw-items-center tw-gap-8">
          <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-2">
            <div className="tw-flex tw-flex-col tw-gap-6">
              <div className="tw-flex">
                <div className="tw-flex tw-items-center tw-gap-2 tw-rounded-[40px] tw-bg-white tw-py-2 tw-pl-2 tw-pr-3 tw-shadow-2xs tw-outline tw-outline-1 tw-outline-offset-0 tw-outline-blue/5">
                  <div className="tw-flex tw-items-center">
                    {RATING_AVATARS.map((src, index) => (
                      <img
                        key={src}
                        src={src}
                        alt=""
                        className={cn(
                          "tw-size-6 tw-rounded-full tw-bg-white tw-object-cover tw-outline tw-outline-1 tw-outline-offset-0 tw-outline-white",
                          index < RATING_AVATARS.length - 1 && "-tw-mr-2.5",
                        )}
                        style={{ zIndex: RATING_AVATARS.length - index }}
                      />
                    ))}
                  </div>
                  <p className="tw-m-0 tw-whitespace-nowrap tw-text-xs tw-font-medium tw-text-blue-700">
                    4.7/5 on <span className="tw-text-primary">G2.com</span>
                  </p>
                </div>
              </div>
              <h2 className="tw-m-0 tw-text-2xl tw-font-semibold tw-leading-8 tw-text-blue">
                Your team and tool, all on the same page
              </h2>
            </div>
            <p className="tw-m-0 tw-text-base tw-leading-6 tw-text-blue-800">
              Brand governance, integrations, automations, and team
              collaboration you need to scale
            </p>
          </div>
          <img
            src={g2Badges}
            alt="G2 High Performer badges"
            className="tw-h-[72px] tw-w-auto tw-shrink-0"
          />
        </div>

        <div className="tw-flex tw-flex-col tw-gap-5 tw-rounded-2xl tw-bg-white tw-p-6 tw-shadow-2xs tw-outline tw-outline-1 tw-outline-offset-0 tw-outline-blue/5">
          <p className={`${EYEBROW_CLASS} tw-text-primary`}>
            Everything in pro, plus:
          </p>
          <div className="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-3">
            {FEATURES.map(({ icon: FeatureIcon, lines }) => (
              <div key={lines[0]} className="tw-flex tw-items-center tw-gap-4">
                <FeatureIcon className="tw-size-5 tw-shrink-0 !tw-fill-none tw-text-primary" />
                <p className="tw-m-0 tw-min-w-0 tw-flex-1 tw-text-base tw-leading-6 tw-text-blue-800">
                  {lines.map((line, index) => (
                    <span key={line}>
                      {index > 0 && <br />}
                      {line}
                    </span>
                  ))}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="tw-flex tw-flex-col tw-gap-4">
          <p className={`${EYEBROW_CLASS} tw-text-blue-500`}>
            Trusted by top companies
          </p>
          <div className="tw-flex tw-items-center tw-gap-4">
            {COMPANY_LOGOS.map((logo) => (
              <div
                key={logo.alt}
                className="tw-flex tw-h-9 tw-min-w-0 tw-flex-1 tw-items-center tw-justify-center"
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  className={cn(
                    "tw-max-w-full tw-object-contain",
                    "className" in logo ? logo.className : "tw-h-9",
                  )}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="tw-flex tw-items-center tw-gap-4">
          <Button
            className="tw-h-10 tw-text-base"
            onClick={() => {
              setIsOverlayVisible(false);
              track("click book a team plan call", {
                "plan name": "Team",
              });
            }}
          >
            <Button.Icon
              icon={
                <IconCalendarTime className="tw-size-5 !tw-fill-none tw-text-violet-50" />
              }
            />
            Book a demo
          </Button>
          <Button
            variant="secondary"
            className="tw-h-10 tw-text-base"
            onClick={close}
          >
            Maybe later
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UpgradeToTeamDialog;
